import { computed, type MaybeRefOrGetter, toValue } from 'vue';

import { useUserStore } from '@/platform/identity/store';
import { useMutation, useQuery } from '@tanstack/vue-query';
import { storeToRefs } from 'pinia';

import {
  clusterMetaApi,
  type CspSummary,
  type ExternalIdResponse,
  type RegionSummary,
} from './api';

import type { ClusterInfo } from './cluster-state';

export const CSP_LIST_KEY = ['csp-list'] as const;
export const REGION_LIST_KEY = ['region-list'] as const;
export const CLUSTER_LIST_KEY = ['cluster', 'cluster-list'] as const;

// Account-scoped per the query-key convention: accountID is the cache
// partition for account-owned data. Logout / timeout / SSO re-auth reload
// the document; plain password login happens before these queries exist, so
// the suffix still acts as a stable partition and
// invalidateQueries({ queryKey: CSP_LIST_KEY }) matches every account
// suffix because vue-query does prefix matching.
// Host-owned CSP list. The sub-side store sentinel fires
// SUB_EVENTS.CONTEXT_INVALIDATED whenever sub mutates its own cluster.cspList;
// the host listener in sub-broadcaster.ts then invalidates this query.
export function useCspListQuery() {
  const { info } = storeToRefs(useUserStore());
  return useQuery<CspSummary[]>({
    queryKey: computed(() => [...CSP_LIST_KEY, info.value?.accountID ?? null] as const),
    queryFn: async () => {
      const res = await clusterMetaApi['/csp/list']();
      return res.list ?? [];
    },
    enabled: computed(() => !!info.value),
    staleTime: 5 * 60 * 1000,
  });
}

// Account-scoped cluster inventory. `enabled` gates the fetch — Support's
// ticket dialog only fetches when the user has cluster@view and the dialog is
// open, mirroring legacy (no cluster-list call otherwise).
//
// staleTime 0 (unlike the 5-min CSP/region lists, which are effectively
// static): cluster inventory is volatile — clusters are created and torn down
// while a session is open, so a picker must reflect current inventory on each
// open. With staleTime 0 the query is always stale, so re-enabling it (reopening
// the dialog) refetches instead of serving a cached list that would drop a
// just-created cluster. Legacy fetched cluster-list on every dialog open.
export function useClusterListQuery(enabled: MaybeRefOrGetter<boolean>) {
  const { info } = storeToRefs(useUserStore());
  return useQuery<ClusterInfo[]>({
    queryKey: computed(() => [...CLUSTER_LIST_KEY, info.value?.accountID ?? null] as const),
    queryFn: async () => {
      const res = await clusterMetaApi['/cluster/cluster-list']();
      return res.list ?? [];
    },
    enabled: computed(() => toValue(enabled) && !!info.value),
    staleTime: 0,
  });
}

export function useRegionListQuery() {
  const { info } = storeToRefs(useUserStore());
  return useQuery<RegionSummary[]>({
    queryKey: computed(() => [...REGION_LIST_KEY, info.value?.accountID ?? null] as const),
    queryFn: async () => {
      const res = await clusterMetaApi['/cluster/region-list']({
        filters: [{ available: true }],
      });
      return res.list ?? [];
    },
    enabled: computed(() => !!info.value),
    staleTime: 5 * 60 * 1000,
  });
}

// One-shot External ID for an IAM role trust policy. A fresh eid each call, so
// it's a mutation fetched on demand (audit-log-export create dialog open).
export function useGetExternalId() {
  return useMutation<ExternalIdResponse, unknown, void>({
    mutationFn: () => clusterMetaApi['/cluster/get-external-id'](),
  });
}
